/**
 * VM-size catalog for the infrastructure onboarding wizard.
 *
 * Lives in its own non-`.tsx` module because:
 *
 *   1. React Fast Refresh requires `.tsx` files to export only
 *      components (rule: react-refresh/only-export-components). The
 *      page used to declare these inline alongside the page component;
 *      moving them here keeps hot-reload functional.
 *
 *   2. Tests want to import the data without pulling the whole page
 *      component graph (axum routing, react-router, i18next, …) into
 *      the test runner.
 *
 * Two invariants surface here:
 *
 *   * **Inference-readiness** — every offered region/zone for every
 *     cloud provider must surface at least one VM size that meets the
 *     minimum spec to host the cross-encoder reranker (Jina v2 base,
 *     ~1.3 GB resident) plus the embedding model (Gemma 300M or planned
 *     embedding-gemma-300M, up to ~1.2 GB resident at FP32). Minimum:
 *     ≥ 4 vCPU AND ≥ 8 GB RAM. AVX-512 is strongly preferred for the
 *     cross-encoder GEMM workload but is informational, not a UI gate.
 *
 *   * **Region-aware filtering (GCP)** — c2 family is not deployed in
 *     europe-north1 (Finland). The dropdown filters c2 out for that
 *     region so terraform apply doesn't fail late with an opaque GCP
 *     API error. Backend (`infra-service`) imposes no GCP allow-list,
 *     so the only place this rule lives is the UI.
 *
 * Source for region availability:
 *   https://cloud.google.com/compute/docs/regions-zones
 */

export type GcpVmFamily = 'e2' | 'n2' | 'n2-highmem' | 'c2'

export interface GcpVmSize {
  value: string
  label: string
  family: GcpVmFamily
}

/**
 * GCP machine types offered to operators provisioning a `gcp-vm` Edge.
 *
 *   * **e2** — cost-optimized, AVX2 only. Fine for general-purpose Edge
 *     workloads (auth, sync workers, admin-mcp). Falls behind sharply
 *     on transformer inference because the ONNX cross-encoder
 *     (Jina v2 base, 278 M params) is GEMM-heavy and AVX2 SIMD lanes
 *     are half the width of AVX-512.
 *
 *   * **n2** — general-purpose with AVX-512 (Cascade Lake / Ice Lake).
 *     Same vCPU/memory shape as e2 at slightly higher cost. Empirically
 *     2–3× faster on cross-encoder rerank at the same vCPU count.
 *     Recommended default for any Edge running intelligence-zone with
 *     `AGION_JINA_RERANKER_ENABLED=true`.
 *
 *   * **c2** — compute-optimized (3.8 GHz Cascade Lake fixed clock +
 *     AVX-512). Best for inference-bound Edge tiers where rerank latency
 *     is the limiting factor. Slightly higher cost per vCPU than n2.
 *     **Not deployed in `europe-north1` (Finland).**
 */
export const GCP_VM_SIZES_ALL: GcpVmSize[] = [
  // e2 — cost-optimized, AVX2 only
  { family: 'e2', value: 'e2-standard-2', label: 'e2-standard-2 (2 vCPU, 8 GB) · AVX2' },
  { family: 'e2', value: 'e2-standard-4', label: 'e2-standard-4 (4 vCPU, 16 GB) · AVX2' },
  { family: 'e2', value: 'e2-standard-8', label: 'e2-standard-8 (8 vCPU, 32 GB) · AVX2' },
  { family: 'e2', value: 'e2-standard-16', label: 'e2-standard-16 (16 vCPU, 64 GB) · AVX2' },
  // n2 — general-purpose with AVX-512
  { family: 'n2', value: 'n2-standard-2', label: 'n2-standard-2 (2 vCPU, 8 GB) · AVX-512' },
  { family: 'n2', value: 'n2-standard-4', label: 'n2-standard-4 (4 vCPU, 16 GB) · AVX-512' },
  { family: 'n2', value: 'n2-standard-8', label: 'n2-standard-8 (8 vCPU, 32 GB) · AVX-512' },
  { family: 'n2', value: 'n2-standard-16', label: 'n2-standard-16 (16 vCPU, 64 GB) · AVX-512' },
  // n2-highmem — same platform, more RAM per vCPU
  { family: 'n2-highmem', value: 'n2-highmem-4', label: 'n2-highmem-4 (4 vCPU, 32 GB) · AVX-512' },
  { family: 'n2-highmem', value: 'n2-highmem-8', label: 'n2-highmem-8 (8 vCPU, 64 GB) · AVX-512' },
  // c2 — compute-optimized
  { family: 'c2', value: 'c2-standard-4', label: 'c2-standard-4 (4 vCPU, 16 GB) · AVX-512, 3.8 GHz' },
  { family: 'c2', value: 'c2-standard-8', label: 'c2-standard-8 (8 vCPU, 32 GB) · AVX-512, 3.8 GHz' },
]

/**
 * Per-region machine-family availability.
 *
 * Verified 2026-05-02 against `gcloud compute machine-types list` for
 * every region in the wizard:
 *
 *   region          | e2 | n2 | n2-highmem | c2
 *   ----------------|----|----|------------|----
 *   europe-north1   | ✓  | ✓  | ✓          | ✓
 *   europe-west1    | ✓  | ✓  | ✓          | ✓
 *   europe-west4    | ✓  | ✓  | ✓          | ✓
 *   us-east1        | ✓  | ✓  | ✓          | ✓
 *   us-central1     | ✓  | ✓  | ✓          | ✓
 *
 * Earlier comments (and an earlier version of this map) claimed c2 was
 * not deployed in europe-north1; that was based on a 2024-era reference
 * and is no longer correct. GCP has rolled c2 out to Finland — verified
 * via `gcloud compute machine-types list --filter "name~^c2-standard-
 * AND zone~^europe-north1-"` returning two zones (-b and -c).
 *
 * This map is still authoritative — if GCP deprecates a family in a
 * specific region, update here and both wizards (host-group and
 * single-host) pick it up automatically. Adding a region to the
 * dropdown without an entry here defaults to the safe e2-only fallback.
 */
export const GCP_REGION_FAMILIES: Record<string, GcpVmFamily[]> = {
  'europe-north1': ['e2', 'n2', 'n2-highmem', 'c2'],
  'europe-west1': ['e2', 'n2', 'n2-highmem', 'c2'],
  'europe-west4': ['e2', 'n2', 'n2-highmem', 'c2'],
  'us-east1': ['e2', 'n2', 'n2-highmem', 'c2'],
  'us-central1': ['e2', 'n2', 'n2-highmem', 'c2'],
}

const GCP_REGION_FALLBACK_FAMILIES: GcpVmFamily[] = ['e2']

/**
 * Return the GCP machine types appropriate for the selected region.
 * Used by both wizard modes (host-group + single-host) so the choice
 * stays consistent with what GCP will actually accept at apply time.
 *
 * Free-typing in the underlying `<datalist>` is preserved — this only
 * shapes the suggestion list. An operator typing `n2-custom-6-24576`
 * still succeeds; the wizard just won't propose it.
 */
export function gcpVmSizesForRegion(region: string): GcpVmSize[] {
  const allowedFamilies = GCP_REGION_FAMILIES[region] ?? GCP_REGION_FALLBACK_FAMILIES
  return GCP_VM_SIZES_ALL.filter(s => allowedFamilies.includes(s.family))
}

export function vcpuCountForVmSize(vmSize: string): number | null {
  const patterns = [
    /(?:^|-)standard-(\d+)$/i,
    /(?:^|-)highmem-(\d+)$/i,
    /(?:^|-)highcpu-(\d+)$/i,
    /(?:^|_)[A-Za-z]+(\d+)[a-z_]*v?\d*$/i,
    /^(\d+)xCPU-/i,
    /^[A-Z]+-(\d+)xCPU-/i,
  ]
  for (const pattern of patterns) {
    const match = pattern.exec(vmSize)
    const raw = match?.[1]
    if (raw) return Number.parseInt(raw, 10)
  }
  return null
}

export function canEnableJinaReranker(vmSize: string): boolean {
  const vcpu = vcpuCountForVmSize(vmSize)
  return vcpu === null || vcpu >= 4
}

/**
 * UpCloud plans surfaced by the wizard.
 *
 * Every plan slug here MUST exist in `upctl server plans`. The first
 * iteration of this list invented several Standard-family slugs
 * (`8xCPU-16GB`, `12xCPU-24GB`, `16xCPU-32GB`, `20xCPU-64GB`) that
 * never existed in the UpCloud catalog — provisioning would have
 * failed at terraform apply with "plan not found". The current list
 * is reconciled against `upctl server plans` output: every slug below
 * is verified-present.
 *
 * Plan family priorities, calibrated against our cross-encoder /
 * embedding workload (Jina v2 base ~1.3 GB resident + Gemma 300M
 * ~110 MB or planned embedding-gemma-300M ~600 MB FP16):
 *
 *   * **PREMIUM** family — UpCloud's newer-silicon tier. AMD EPYC
 *     deployment, with the [Turin / Zen 5 rollout that started Apr 2025]
 *     (https://upcloud.com/docs/changelog/2025-04-03-epyc-turin/)
 *     bringing AVX-512 + VNNI + BFloat16 + AVX-512-FP16 to the line.
 *     Recommended for any Edge running intelligence-zone with
 *     `AGION_JINA_RERANKER_ENABLED=true`. Verify silicon on first boot
 *     (`cat /proc/cpuinfo | grep -E "model name|avx512"`) — Turin is
 *     a rolling deployment and not all Helsinki hosts are on Zen 5
 *     yet. EPYC 9575F or any 9xxx is what we want; 7542 (Rome / Zen 2)
 *     and 7543 (Milan / Zen 3) lack AVX-512 and put the box back into
 *     the same envelope as the older Standard tier.
 *
 *   * **General Purpose (Standard)** — older silicon (commonly
 *     EPYC 7542 / 7543, AVX2-only). Functional for inference but
 *     ~2-3× slower per-core on the cross-encoder than Premium. Kept
 *     for non-intelligence Edges (ingestion-only, gateways, auth
 *     proxies) and as a fallback when Premium isn't available.
 *
 *   * **High Memory** family — also AMD silicon, currently AVX2-only
 *     in our zones. Useful when the embedding model's working set is
 *     the binding constraint and the cross-encoder isn't enabled
 *     (e.g. an ingestion-only Edge running batch embedding).
 *
 *   * **High CPU (HICPU)** — AVX2-only currently; same generation as
 *     General Purpose. Listed despite the AVX2 limitation because the
 *     fixed-clock characteristic still beats General Purpose at
 *     small-batch latency on workloads that don't lean on AVX-512.
 *
 * Smaller plans (1xCPU-2GB, 2xCPU-4GB) are kept because they're valid
 * for non-intelligence Edges. The `· inference` suffix marks plans
 * meeting the ≥ 4 vCPU + ≥ 8 GB minimum to run the full
 * intelligence-zone with both models loaded.
 *
 * UpCloud plan availability is broadly uniform across the zones we
 * offer (fi-hel1, fi-hel2, de-fra1, nl-ams1, uk-lon1, us-nyc1) — no
 * per-zone filter needed today. If UpCloud later restricts a SKU to
 * specific zones (e.g. Turin-only zones) add a `UPCLOUD_ZONE_PLANS`
 * map mirroring the GCP region filter pattern.
 */
//
// `storageGb` is the storage included in the plan price (`upctl server
// plans`, storage_size, verified 2026-09-17). New VMs start with that disk.
export const UPCLOUD_PLANS = [
  // Below the inference threshold — fine for non-intelligence Edges
  { value: '1xCPU-1GB', label: '1 vCPU, 1 GB, 25 GB', storageGb: 25 },
  { value: '1xCPU-2GB', label: '1 vCPU, 2 GB, 50 GB', storageGb: 50 },
  { value: '2xCPU-4GB', label: '2 vCPU, 4 GB, 80 GB', storageGb: 80 },

  // Premium tier — recommended for inference (AVX-512 if Turin)
  { value: 'PREMIUM-4xCPU-8GB', label: 'Premium 4 vCPU, 8 GB, 100 GB · inference (tight)', storageGb: 100 },
  { value: 'PREMIUM-4xCPU-16GB', label: 'Premium 4 vCPU, 16 GB, 150 GB · inference, AVX-512 if Turin', storageGb: 150 },
  { value: 'PREMIUM-4xCPU-32GB', label: 'Premium 4 vCPU, 32 GB, 200 GB · inference, high-mem', storageGb: 200 },
  { value: 'PREMIUM-8xCPU-16GB', label: 'Premium 8 vCPU, 16 GB, 200 GB · inference', storageGb: 200 },
  { value: 'PREMIUM-8xCPU-32GB', label: 'Premium 8 vCPU, 32 GB, 300 GB · inference', storageGb: 300 },
  { value: 'PREMIUM-8xCPU-64GB', label: 'Premium 8 vCPU, 64 GB, 300 GB · inference, high-mem', storageGb: 300 },
  { value: 'PREMIUM-16xCPU-32GB', label: 'Premium 16 vCPU, 32 GB, 300 GB · inference, multi-tenant', storageGb: 300 },
  { value: 'PREMIUM-16xCPU-64GB', label: 'Premium 16 vCPU, 64 GB, 300 GB · inference, multi-tenant', storageGb: 300 },

  // General Purpose (Standard) — older AMD silicon, AVX2-only commonly
  { value: '4xCPU-8GB', label: '4 vCPU, 8 GB, 160 GB · inference (slow, AVX2 only)', storageGb: 160 },
  { value: '6xCPU-16GB', label: '6 vCPU, 16 GB, 320 GB · inference (slow, AVX2 only)', storageGb: 320 },
  { value: '8xCPU-32GB', label: '8 vCPU, 32 GB, 640 GB · inference (slow, AVX2 only)', storageGb: 640 },
  { value: '12xCPU-48GB', label: '12 vCPU, 48 GB, 960 GB · inference (slow, AVX2 only)', storageGb: 960 },
  { value: '16xCPU-64GB', label: '16 vCPU, 64 GB, 1280 GB · inference (slow, AVX2 only)', storageGb: 1280 },

  // High Memory — for embedding-heavy Edges where rerank is disabled
  { value: 'HIMEM-4xCPU-32GB', label: 'High Memory 4 vCPU, 32 GB, 100 GB · embedding-only', storageGb: 100 },
  { value: 'HIMEM-4xCPU-64GB', label: 'High Memory 4 vCPU, 64 GB, 200 GB · embedding-only, high-mem', storageGb: 200 },
  { value: 'HIMEM-8xCPU-192GB', label: 'High Memory 8 vCPU, 192 GB, 400 GB · large-corpus', storageGb: 400 },

  // High CPU — AVX2-only but consistent clock
  { value: 'HICPU-8xCPU-12GB', label: 'High CPU 8 vCPU, 12 GB, 100 GB · CPU-bound non-AVX-512', storageGb: 100 },
  { value: 'HICPU-8xCPU-16GB', label: 'High CPU 8 vCPU, 16 GB, 200 GB · CPU-bound non-AVX-512', storageGb: 200 },
  { value: 'HICPU-16xCPU-32GB', label: 'High CPU 16 vCPU, 32 GB, 200 GB · CPU-bound non-AVX-512', storageGb: 200 },
]

/** Storage an UpCloud plan already pays for; undefined for a plan typed by hand. */
export function upcloudPlanStorageGb(plan: string): number | undefined {
  return UPCLOUD_PLANS.find((p) => p.value === plan)?.storageGb
}

/**
 * Azure VM sizes surfaced by the wizard.
 *
 * Pre-2026 the list was D-series v3 only (Skylake-era, 2017 silicon).
 * v5 generations use Cascade Lake / Ice Lake (Intel) or Milan / Genoa
 * (AMD) and are significantly faster on the cross-encoder workload at
 * the same vCPU count.
 *
 *   * **Dsv5** — the documented Edge tiers (D2s_v5 test, D4s_v5
 *     production default, D8s_v5 heavy retrieval). The Azure onboarding
 *     runbooks preflight quota for this family only ("Standard Dsv5
 *     Family vCPUs"), and the azure-vm module default is Standard_D2s_v5.
 *   * **Ddsv5** — Intel Ice Lake with local temp SSD. Full AVX-512
 *     including VNNI for int8 inference. Recommended Intel path when
 *     inference throughput matters. Draws from its own "Standard DDSv5
 *     Family" quota, which the onboarding preflight does NOT cover —
 *     confirm quota before selecting.
 *   * **Dadsv5** — AMD EPYC Genoa. AVX-512 available; some workloads see
 *     lower per-core throughput than Intel Ice Lake but cost per vCPU
 *     is lower. Reasonable fallback when Ddsv5 capacity is constrained.
 *     Same caveat: separate "Standard DADSv5 Family" quota.
 *   * **Dsv3** — kept for backward compat with environments provisioned
 *     pre-2026; new Edges should prefer v5 even though v3 still works.
 *
 * All three Azure regions in `AZURE_REGIONS` (swedencentral,
 * northeurope, westeurope) have Dsv5, Ddsv5, and Dadsv5 GA
 * (re-verified 2026-08-11 via az; see the snapshot in
 * `InfraOnboardingWizardPage.gcpVmSizes.test.ts`). No region-aware
 * filter needed for Azure today; if Dpdsv6 (Sapphire Rapids w/ AMX)
 * gets surfaced later it will need one.
 */
export const AZURE_VM_SIZES = [
  // v5 — documented Edge tiers (Dsv5). Matches the onboarding quota preflight.
  { value: 'Standard_D2s_v5', label: 'D2s v5 (2 vCPU, 8 GB) · test/small' },
  { value: 'Standard_D4s_v5', label: 'D4s v5 (4 vCPU, 16 GB) · production default · inference' },
  { value: 'Standard_D8s_v5', label: 'D8s v5 (8 vCPU, 32 GB) · heavy retrieval · inference' },
  // v5 — Intel Ice Lake (Ddsv5), local temp SSD. Separate DDSv5 quota family.
  { value: 'Standard_D4ds_v5', label: 'D4ds v5 (4 vCPU, 16 GB) · Intel Ice Lake, AVX-512 · inference' },
  { value: 'Standard_D8ds_v5', label: 'D8ds v5 (8 vCPU, 32 GB) · Intel Ice Lake, AVX-512 · inference' },
  { value: 'Standard_D16ds_v5', label: 'D16ds v5 (16 vCPU, 64 GB) · Intel Ice Lake, AVX-512 · inference' },
  // v5 — AMD EPYC Genoa (Dadsv5). Lower cost per vCPU. Separate DADSv5 quota family.
  { value: 'Standard_D4ads_v5', label: 'D4ads v5 (4 vCPU, 16 GB) · AMD EPYC Genoa, AVX-512 · inference' },
  { value: 'Standard_D8ads_v5', label: 'D8ads v5 (8 vCPU, 32 GB) · AMD EPYC Genoa, AVX-512 · inference' },
  // v3 — legacy Skylake; OK for non-intelligence Edges
  { value: 'Standard_D2s_v3', label: 'D2s v3 (2 vCPU, 8 GB) · legacy' },
  { value: 'Standard_D4s_v3', label: 'D4s v3 (4 vCPU, 16 GB) · legacy · inference (slow)' },
  { value: 'Standard_D8s_v3', label: 'D8s v3 (8 vCPU, 32 GB) · legacy · inference (slow)' },
]

// ============================================================================
// Managed-database plan catalogs
// ============================================================================
//
// DB plans live next to VM plans because both are operator-visible SKU lists
// fed into the same wizard, and the test pinning (gcpVmSizes.test.ts) needs
// to import them without tripping `react-refresh/only-export-components` in
// the page .tsx. The wizard exposes these via the EditableCombobox so an
// operator can paste any plan code from `upctl database plans pg` (UpCloud)
// or the Azure / GCP catalogs that we don't enumerate.

/**
 * UpCloud Managed PostgreSQL plan codes — full catalog, mirrored from
 * `upctl database plans pg`.
 *
 * Format: `{nodes}x{cores}xCPU-{ram}GB-{disk}GB`. Single-node (1x) for
 * Dev / non-production; 2-node and 3-node tiers for HA. There is no
 * `1x4xCPU-…` or `1x8xCPU-…` SKU — single-node tops out at
 * `1x2xCPU-4GB-100GB`.
 *
 * Source of truth: `upctl database plans pg` against the operator's
 * UpCloud account. If UpCloud adds, removes, or renames SKUs, run that
 * command and sync this list. The pin test in
 * `InfraOnboardingWizardPage.gcpVmSizes.test.ts` enforces plan-code
 * shape; it does not enforce catalog completeness.
 */
export const UPCLOUD_DB_PLANS = [
  // Single-node (Dev / non-production)
  { value: '1x1xCPU-1GB-10GB', label: 'Dev — 1 vCPU, 1 GB, 10 GB (single node)' },
  { value: '1x1xCPU-2GB-25GB', label: 'Dev — 1 vCPU, 2 GB, 25 GB (single node)' },
  { value: '1x2xCPU-4GB-50GB', label: 'Dev — 2 vCPU, 4 GB, 50 GB (single node)' },
  { value: '1x2xCPU-4GB-100GB', label: 'Dev — 2 vCPU, 4 GB, 100 GB (single node)' },

  // 2-node HA (production)
  { value: '2x2xCPU-4GB-50GB', label: 'HA — 2 vCPU, 4 GB, 50 GB (2 nodes)' },
  { value: '2x2xCPU-4GB-100GB', label: 'HA — 2 vCPU, 4 GB, 100 GB (2 nodes)' },
  { value: '2x4xCPU-8GB-50GB', label: 'HA — 4 vCPU, 8 GB, 50 GB (2 nodes)' },
  { value: '2x4xCPU-8GB-100GB', label: 'HA — 4 vCPU, 8 GB, 100 GB (2 nodes)' },
  { value: '2x6xCPU-16GB-100GB', label: 'HA — 6 vCPU, 16 GB, 100 GB (2 nodes)' },
  { value: '2x6xCPU-16GB-250GB', label: 'HA — 6 vCPU, 16 GB, 250 GB (2 nodes)' },
  { value: '2x8xCPU-32GB-100GB', label: 'HA — 8 vCPU, 32 GB, 100 GB (2 nodes)' },
  { value: '2x8xCPU-32GB-250GB', label: 'HA — 8 vCPU, 32 GB, 250 GB (2 nodes)' },
  { value: '2x8xCPU-32GB-500GB', label: 'HA — 8 vCPU, 32 GB, 500 GB (2 nodes)' },
  { value: '2x16xCPU-64GB-1000GB', label: 'HA — 16 vCPU, 64 GB, 1 TB (2 nodes)' },
  { value: '2x16xCPU-64GB-1500GB', label: 'HA — 16 vCPU, 64 GB, 1.5 TB (2 nodes)' },
  { value: '2x20xCPU-128GB-2000GB', label: 'HA — 20 vCPU, 128 GB, 2 TB (2 nodes)' },

  // 3-node HA (production with quorum)
  { value: '3x2xCPU-4GB-100GB', label: 'HA — 2 vCPU, 4 GB, 100 GB (3 nodes)' },
  { value: '3x2xCPU-4GB-200GB', label: 'HA — 2 vCPU, 4 GB, 200 GB (3 nodes)' },
  { value: '3x4xCPU-8GB-100GB', label: 'HA — 4 vCPU, 8 GB, 100 GB (3 nodes)' },
  { value: '3x4xCPU-8GB-200GB', label: 'HA — 4 vCPU, 8 GB, 200 GB (3 nodes)' },
  { value: '3x6xCPU-16GB-200GB', label: 'HA — 6 vCPU, 16 GB, 200 GB (3 nodes)' },
  { value: '3x6xCPU-16GB-500GB', label: 'HA — 6 vCPU, 16 GB, 500 GB (3 nodes)' },
  { value: '3x8xCPU-32GB-200GB', label: 'HA — 8 vCPU, 32 GB, 200 GB (3 nodes)' },
  { value: '3x8xCPU-32GB-500GB', label: 'HA — 8 vCPU, 32 GB, 500 GB (3 nodes)' },
  { value: '3x8xCPU-32GB-1000GB', label: 'HA — 8 vCPU, 32 GB, 1 TB (3 nodes)' },
  { value: '3x16xCPU-64GB-1000GB', label: 'HA — 16 vCPU, 64 GB, 1 TB (3 nodes)' },
  { value: '3x16xCPU-64GB-2000GB', label: 'HA — 16 vCPU, 64 GB, 2 TB (3 nodes)' },
  { value: '3x20xCPU-128GB-2000GB', label: 'HA — 20 vCPU, 128 GB, 2 TB (3 nodes)' },
]

/** Azure Database for PostgreSQL Flexible Server SKU codes — burstable + general-purpose Ddsv5. */
export const AZURE_DB_SKUS = [
  { value: 'B_Standard_B2s', label: 'B2s (2 vCPU, 4 GB) · burstable' },
  { value: 'GP_Standard_D2ds_v5', label: 'D2ds v5 (2 vCPU, 8 GB)' },
  { value: 'GP_Standard_D4ds_v5', label: 'D4ds v5 (4 vCPU, 16 GB)' },
  { value: 'GP_Standard_D8ds_v5', label: 'D8ds v5 (8 vCPU, 32 GB)' },
]

/** GCP Cloud SQL custom machine types (`db-custom-{cores}-{ramMB}`). */
export const GCP_DB_TIERS = [
  { value: 'db-custom-1-3840', label: 'db-custom-1-3840 (1 vCPU, 3.75 GB)' },
  { value: 'db-custom-2-8192', label: 'db-custom-2-8192 (2 vCPU, 8 GB)' },
  { value: 'db-custom-4-16384', label: 'db-custom-4-16384 (4 vCPU, 16 GB)' },
  { value: 'db-custom-8-32768', label: 'db-custom-8-32768 (8 vCPU, 32 GB)' },
]
